import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { ErrorPage } from '@/components/common/ErrorPage';
import { Label, LABEL_TYPE, Spinner } from '@/components/generic';
import { ConfirmationModal } from '@/components/modals/ConfirmationModal';
import { queryClient } from '@/configuration/react-query';
import { AuthContext, EventProvider, OfflineContext } from '@/contexts';
import { PERMISSION_TYPE, TABS } from '@/enums';
import { getPermissionsForOrganization } from '@/helpers';
import { formatDate } from '@/helpers/date';
import { useMobileScreen } from '@/hooks';
import { AssetsContainer } from '@/pages/Event/Asset/AssetsContainer';
import { CeremonyTemplateView } from '@/pages/Event/CeremonyTemplate/CeremonyTemplateContainer';
import { CoverPhotos } from '@/pages/Event/CoverPhotos';
import { EventDetail } from '@/pages/Event/EventDetail';
import { Header } from '@/pages/Event/Header';
import { LiveStream } from '@/pages/Event/LiveStream/LiveStream';
import { MusicTab } from '@/pages/Event/Music/Music';
import { TextSection } from '@/pages/Event/Text';
import { TextOverlay } from '@/pages/Event/TextOverlay/TextOverlay';
import { TributeVideos } from '@/pages/Event/TributeVideo';
import { EVENT_CURRENT_TAB_STORAGE_KEY, getStoredEventTab } from '@/pages/Event/eventTabStorage';
import {
  useDeleteEventMutation,
  useGetEventByIdQuery,
  useGetOrganizationByIdSimplifiedQuery
} from '@/services';

export const EventView = () => {
  const { eventId } = useParams();
  const [error, setError] = useState<number>();
  const {
    data: eventData,
    isLoading,
    isError
  } = useGetEventByIdQuery(Number(eventId), {
    onSuccess: () => undefined,
    onError: (error) => setError(error.status)
  });
  const navigate = useNavigate();
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState<boolean>(false);
  const isMobile = useMobileScreen();
  const [t] = useTranslation('common');
  const { permissions, organizationPermissions: userOrganizationPermissions } =
    useContext(AuthContext);
  const { isOnline } = useContext(OfflineContext);
  const hasOrgAccess = permissions.includes(PERMISSION_TYPE.ORG_ACCESS);

  const { data: eventOrganization } = useGetOrganizationByIdSimplifiedQuery(
    Number(eventData?.data?.organization?.id)
  );
  const organizationPermissions = useMemo(
    () => getPermissionsForOrganization(userOrganizationPermissions, eventOrganization?.data),
    [eventOrganization, userOrganizationPermissions]
  );

  const [currentTab, setCurrentTab] = useState<string>(TABS.DETAILS);

  const { mutate: deleteEvent } = useDeleteEventMutation(queryClient, {
    onSuccess: () => {
      setIsOpenDeleteModal(false);
      handleCloseDeleteModal();
    },
    onError: () => {
      setIsOpenDeleteModal(false);
    }
  });

  const handleCloseDeleteModal = () => {
    setIsOpenDeleteModal(false);
    navigate('events');
  };

  const { offlineEvents } = useContext(OfflineContext);
  const offlineEventData = offlineEvents.find((e) => e.id === Number(eventId));
  const shouldShowLoading = isOnline && isLoading;
  const shouldShowError = isOnline && !!error;

  const content = () => {
    if (!isOnline && currentTab === TABS.DETAILS) {
      if (!offlineEventData) {
        return (
          <div className='flex h-full items-center justify-center'>
            <Label
              text={t('noOfflineEventData')}
              type={LABEL_TYPE.PARAGRAPH2}
              className='text-secondary-steel-blue'
            />
          </div>
        );
      }
      return (
        <div className='flex w-full flex-col gap-4 px-4 py-6 md:px-20'>
          {offlineEventData.name && (
            <div className='flex w-full flex-col md:flex-row md:items-center'>
              <Label
                text={`${t('name')}:`}
                type={LABEL_TYPE.PARAGRAPH1}
                className='pb-2.5 text-secondary-gray sm:p-0 md:w-1/6'
              />
              <Label
                text={offlineEventData.name}
                type={LABEL_TYPE.PARAGRAPH1}
                className='!text-primary-black'
              />
            </div>
          )}
          {offlineEventData.startDateAndTime && (
            <div className='flex w-full flex-col md:flex-row md:items-center'>
              <Label
                text={`${t('startDateAndTime')}:`}
                type={LABEL_TYPE.PARAGRAPH1}
                className='pb-2.5 text-secondary-gray sm:p-0 md:w-1/6'
              />
              <Label
                text={formatDate(new Date(offlineEventData.startDateAndTime))}
                type={LABEL_TYPE.PARAGRAPH1}
                className='!text-primary-black'
              />
            </div>
          )}
          {offlineEventData.description && (
            <div className='flex w-full flex-col md:flex-row md:items-center'>
              <Label
                text={`${t('description')}:`}
                type={LABEL_TYPE.PARAGRAPH1}
                className='pb-2.5 text-secondary-gray sm:p-0 md:w-1/6'
              />
              <Label
                text={offlineEventData.description}
                type={LABEL_TYPE.PARAGRAPH1}
                className='!text-primary-black'
              />
            </div>
          )}
          {offlineEventData.organizationName && (
            <div className='flex w-full flex-col md:flex-row md:items-center'>
              <Label
                text={`${t('organization')}:`}
                type={LABEL_TYPE.PARAGRAPH1}
                className='pb-2.5 text-secondary-gray sm:p-0 md:w-1/6'
              />
              <Label
                text={offlineEventData.organizationName}
                type={LABEL_TYPE.PARAGRAPH1}
                className='!text-primary-black'
              />
            </div>
          )}
          {offlineEventData.status && (
            <div className='flex w-full flex-col md:flex-row md:items-center'>
              <Label
                text={`${t('status')}:`}
                type={LABEL_TYPE.PARAGRAPH1}
                className='pb-2.5 text-secondary-gray sm:p-0 md:w-1/6'
              />
              <Label
                text={offlineEventData.status}
                type={LABEL_TYPE.PARAGRAPH1}
                className='!text-primary-black'
              />
            </div>
          )}
          <div className='flex w-full flex-col md:flex-row md:items-center'>
            <Label
              text={`${t('downloadedSongs')}:`}
              type={LABEL_TYPE.PARAGRAPH1}
              className='pb-2.5 text-secondary-gray sm:p-0 md:w-1/6'
            />
            <Label
              text={String(offlineEventData.songs.length)}
              type={LABEL_TYPE.PARAGRAPH1}
              className='!text-primary-black'
            />
          </div>
        </div>
      );
    }

    switch (currentTab) {
      case TABS.DETAILS:
        return <EventDetail organizationPermissions={organizationPermissions} />;
      case TABS.UPLOADED_CONTENT:
        return (
          <AssetsContainer
            eventId={eventId ?? '0'}
            className='!mt-4 h-full md:mt-9 lg:px-20'
            assetContainerStyles='!h-full'
            showContentManagementAccessPopup={true}
            organizationPermissions={organizationPermissions}
          />
        );
      case TABS.COVER_PHOTOS:
        return <CoverPhotos organizationPermissions={organizationPermissions} />;
      case TABS.TRIBUTE_VIDEOS:
        return (
          <TributeVideos id={eventId ?? '0'} organizationPermissions={organizationPermissions} />
        );
      case TABS.TEXT:
        return <TextSection organizationPermissions={organizationPermissions} />;
      case TABS.CEREMONY:
        return (
          <CeremonyTemplateView
            eventId={eventId ?? '0'}
            organizationPermissions={organizationPermissions}
          />
        );
      case TABS.MUSIC:
        return <MusicTab organizationPermissions={organizationPermissions} />;
      case TABS.LIVE_STREAM:
        return <LiveStream organizationPermissions={organizationPermissions} />;
      case TABS.TEXT_OVERLAY:
        return <TextOverlay />;
      default:
        return '';
    }
  };
  useEffect(() => {
    if (isMobile && !hasOrgAccess) {
      setCurrentTab(TABS.TRIBUTE_VIDEOS);
    }
  }, [isMobile]);

  useEffect(() => {
    if (!isOnline) {
      setCurrentTab(TABS.DETAILS);
    }
  }, [isOnline]);

  useEffect(() => {
    if (!isOnline) {
      return;
    }
    const storedTab = getStoredEventTab();
    localStorage.setItem(EVENT_CURRENT_TAB_STORAGE_KEY, storedTab);
    setCurrentTab(storedTab);
  }, [isOnline]);

  return (
    <div className='flex h-screen w-full flex-col items-center px-3'>
      {shouldShowLoading ? (
        <Spinner />
      ) : shouldShowError ? (
        <ErrorPage code={error} />
      ) : (
        (!isOnline || !isError) && (
          <EventProvider eventId={Number(eventId)}>
            {hasOrgAccess ? (
              <Header
                currentTab={currentTab}
                setCurrentTab={setCurrentTab}
                setIsDeleteModalOpen={setIsOpenDeleteModal}
                isOffline={!isOnline}
                offlineEventName={offlineEventData?.name ?? ''}
              />
            ) : (
              <div>
                <Label
                  text={t('tributeVideos')}
                  type={LABEL_TYPE.H2}
                  className='mt-4 pb-4 text-[22px] md:hidden'
                />
                <Label
                  type={LABEL_TYPE.PARAGRAPH2}
                  text={t('pleaseCreateTributeVideo')}
                  className='pb-6 font-sans text-secondary-gray md:hidden'
                />
              </div>
            )}
            {content()}
            {eventId && (
              <ConfirmationModal
                isOpen={isOpenDeleteModal}
                message={t('deleteMessage', { item: eventData?.data?.name ?? '' })}
                onClose={handleCloseDeleteModal}
                onConfirm={() => deleteEvent(eventData?.data?.id)}
              />
            )}
          </EventProvider>
        )
      )}
    </div>
  );
};
